/* eslint-disable  */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, Grid, Typography, Divider, Chip, Box, Stack } from '@mui/material';
import secureLocalStorage from 'react-secure-storage';
import { themeColors } from 'views/ConstantColors';

const buildInfo = {
    commit: process.env.REACT_APP_GIT_COMMIT || 'Unknown',
    branch: process.env.REACT_APP_GIT_BRANCH || 'Unknown',
    buildTime: process.env.REACT_APP_BUILD_TIME || new Date().toISOString()
};

const Demographics = () => {
    const [userRoles, setUserRoles] = useState([]);

    useEffect(() => {
        const demoData = secureLocalStorage.getItem('user_roles');
        if (demoData) {
            try {
                const parsedData = JSON.parse(demoData);
                setUserRoles(parsedData);
            } catch (error) {
                console.error('Error parsing user data:', error);
            }
        }
    }, []);

    if (!userRoles.length) {
        return null;
    }

    // Get user data from the first role entry since personal info is same across roles
    const userData = userRoles[0]?.user || {};

    const renderDepartmentOrBranchInfo = (roleData) => {
        return (
            <Stack spacing={1}>
                <Typography variant="subtitle1" color={themeColors.primary.dark} sx={{ fontWeight: 600 }}>
                    Department Information
                </Typography>
                <Typography variant="body2" color={themeColors.grey[700]}>
                    <strong>Department:</strong> {roleData.department?.name || 'N/A'}
                </Typography>
                <Typography variant="body2" color={themeColors.grey[700]}>
                    <strong>Department Code:</strong> {roleData.department?.code || 'N/A'}
                </Typography>

                <Typography variant="subtitle1" color={themeColors.primary.dark} sx={{ fontWeight: 600, mt: 2 }}>
                    Branch Information
                </Typography>
                <Typography variant="body2" color={themeColors.grey[700]}>
                    <strong>Branch:</strong> {roleData.branchCode?.name || 'N/A'}
                </Typography>
                <Typography variant="body2" color={themeColors.grey[700]}>
                    <strong>Code:</strong> {roleData.branchCode?.code || 'N/A'}
                </Typography>
                <Typography variant="body2" color={themeColors.grey[700]}>
                    <strong>Description:</strong> {roleData.branchCode?.description || 'N/A'}
                </Typography>
            </Stack>
        );
    };

    return (
        <Card
            sx={{
                borderRadius: 2,
                boxShadow: `0 2px 8px ${themeColors.grey[200]}`,
                '&:hover': {
                    boxShadow: `0 4px 12px ${themeColors.grey[300]}`
                },
                transition: 'box-shadow 0.3s ease-in-out'
            }}
        >
            <CardContent>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h4" color={themeColors.primary.main} gutterBottom>
                            Employee Information
                        </Typography>
                        <Divider sx={{ mb: 3, borderColor: themeColors.grey[200] }} />
                    </Grid>

                    {/* Personal Information */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="h5" gutterBottom color={themeColors.primary.main}>
                            Personal Details
                        </Typography>
                        <Stack spacing={1}>
                            <Typography variant="body1" color={themeColors.grey[800]}>
                                <strong>Full Name:</strong> {userData.firstName || 'N/A'} {userData.lastName || ''}
                            </Typography>
                            <Typography variant="body1" color={themeColors.grey[800]}>
                                <strong>Email:</strong> {userData.email || 'N/A'}
                            </Typography>
                            <Typography variant="body1" color={themeColors.grey[800]}>
                                <strong>Employee Number:</strong> {userData.employeeNumber || 'N/A'}
                            </Typography>
                            <Typography variant="body1" color={themeColors.grey[800]}>
                                <strong>Position:</strong> {userData.employeeTitle || 'N/A'}
                            </Typography>
                            <Typography variant="body1" color={themeColors.grey[800]}>
                                <strong>Status:</strong> {userData.status || 'N/A'}
                            </Typography>
                        </Stack>

                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" color={themeColors.grey[800]} sx={{ mb: 1 }}>
                                <strong>Assigned Roles:</strong>
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                {userRoles.map((roleData) => (
                                    <Chip
                                        key={roleData.id}
                                        label={(roleData.role?.name || 'UNKNOWN').replace('ROLE_', '')}
                                        sx={{
                                            backgroundColor: themeColors.primary.lighter,
                                            color: themeColors.primary.dark,
                                            fontWeight: 600,
                                            borderRadius: '8px',
                                            '&:hover': {
                                                backgroundColor: themeColors.primary.light,
                                                color: themeColors.primary.main
                                            },
                                            transition: 'all 0.2s ease-in-out'
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    </Grid>

                    {/* Department Information */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="h5" gutterBottom color={themeColors.primary.main}>
                            Role-specific Information
                        </Typography>
                        <Stack spacing={2}>
                            {userRoles.map((roleData, index) => (
                                <Box
                                    key={roleData.id}
                                    sx={{
                                        p: 2,
                                        backgroundColor: index % 2 === 0 ? themeColors.primary.lighter : themeColors.background.default,
                                        borderRadius: '8px',
                                        border: `1px solid ${themeColors.primary.lighter}`,
                                        '&:hover': {
                                            borderColor: themeColors.primary.light,
                                            backgroundColor: index % 2 === 0 ? themeColors.primary.lighter : themeColors.grey[50]
                                        },
                                        transition: 'all 0.2s ease-in-out'
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            mb: 1,
                                            fontWeight: 600,
                                            color: themeColors.primary.main
                                        }}
                                    >
                                        {(roleData.role?.name || 'UNKNOWN').replace('ROLE_', '')}
                                    </Typography>
                                    {renderDepartmentOrBranchInfo(roleData)}
                                </Box>
                            ))}
                        </Stack>
                    </Grid>

                    {/* System Information */}
                    <Grid item xs={12}>
                        <Divider sx={{ my: 2, borderColor: themeColors.grey[200] }} />
                        <Typography variant="h5" gutterBottom color={themeColors.primary.main}>
                            System Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Stack spacing={1}>
                                    <Typography variant="body1" color={themeColors.grey[800]}>
                                        <strong>Created At:</strong>{' '}
                                        {userRoles[0]?.createdAt ? new Date(userRoles[0].createdAt).toLocaleString() : 'N/A'}
                                    </Typography>
                                    <Typography variant="body1" color={themeColors.grey[800]}>
                                        <strong>Created By:</strong> {userRoles[0]?.createdBy || 'N/A'}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Stack spacing={1}>
                                    <Typography variant="body1" color={themeColors.grey[800]}>
                                        <strong>Last Modified:</strong>{' '}
                                        {userRoles[0]?.lastModifiedAt ? new Date(userRoles[0].lastModifiedAt).toLocaleString() : 'N/A'}
                                    </Typography>
                                    <Typography variant="body1" color={themeColors.grey[800]}>
                                        <strong>Modified By:</strong> {userRoles[0]?.lastModifiedBy || 'N/A'}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Stack spacing={1}>
                                    <Typography variant="body1" color={themeColors.grey[800]}>
                                        <strong>Commit:</strong> {buildInfo.commit}
                                    </Typography>
                                    <Typography variant="body1" color={themeColors.grey[800]}>
                                        <strong>Branch:</strong> {buildInfo.branch}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Stack spacing={1}>
                                    <Typography variant="body1" color={themeColors.grey[800]}>
                                        <strong>Build Time:</strong> {buildInfo.buildTime}
                                    </Typography>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default Demographics;
